import React, { useState, useMemo } from "react";
import InsuranceComponent from "..";
import RegistarationComponent from "./RegistarationComponent";
import RefundProtectionInfo from "./RefundProtectionInfo";
import { INSURANCE } from "../../../../Enums/Enum";
import setting from '../../../../config/settings';

const { viClientId } = setting.api

const RegistrationCancellation = React.memo(({ updateInsuranceQuotes, allPrograms, camperList, insuranceState, userEmail, userName, userLastName, billingDetail, isDirectorView }) => {
    const [localQuotes, setLocalQuotes] = useState([]);
    const [showSelectionError, setShowSelectionError] = useState(false);

    // Build participant list in the same order as the registration-cancellation DOM elements
    const participants = useMemo(() => {
        const list = [];
        allPrograms.forEach(item => {
            (item.campers || []).forEach(camper => {
                const camperDetails = camperList?.find(c => c._id === camper.camperId);
                list.push({
                    name: `${camperDetails?.firstName ?? ''} ${camperDetails?.lastName ?? ''}`.trim(),
                    description: `${item.campName} - ${item.programName}`,
                });
            });
        });
        return list;
    }, [allPrograms, camperList]);

    const handleOfferStateChange = (e) => {
        setTimeout(() => {
            const offers = document.querySelectorAll(`multi-offer[product="${INSURANCE.REGISTRATION_CANCELLATION_INSURANCE}"]`);
            const allQuotes = [];
            offers.forEach(offer => {
                // Monkey-patch validate() once so our custom selections satisfy it
                if (!offer._customValidatePatched) {
                    offer.validate = async function () {
                        const allSelected = this.quotes?.every((_, idx) =>
                            this._customSelections?.[idx] !== undefined
                        );
                        if (allSelected) {
                            setShowSelectionError(false);
                            return { isValid: true };
                        }
                        setShowSelectionError(true);
                        return { isValid: false };
                    };
                    offer._customValidatePatched = true;
                }
                if (offer.quotes?.length) {
                    offer.quotes.forEach(q => {
                        if (q.quote) {
                            allQuotes.push({
                                quoteId: q.quote.quote_id,
                                price: q.quote.total / 100,
                                selectedState: q.selectedState,
                            });
                        }
                    });
                }
            });
            setLocalQuotes(allQuotes);
        }, 10);
        updateInsuranceQuotes(e);
    };

    const handleQuoteSelection = (quoteIdx, isAccept) => {
        setShowSelectionError(false);
        const state = isAccept ? 'ACCEPTED' : 'DECLINED';
        const offer = document.querySelector(`multi-offer[product="${INSURANCE.REGISTRATION_CANCELLATION_INSURANCE}"]`);

        if (offer) {
            // Track selection so monkey-patched validate() can pass
            offer._customSelections = offer._customSelections || {};
            offer._customSelections[quoteIdx] = state;

            // Directly update quotes array so updateInsuranceQuotes reads correct totals
            if (offer.quotes?.[quoteIdx]) {
                offer.quotes[quoteIdx].selectedState = state;
            }

            // Dispatch event to trigger updateInsuranceQuotes in parent
            offer.dispatchEvent(new CustomEvent('offer-state-change', { bubbles: true, composed: true }));
        }

        // Best-effort: try clicking shadow DOM radio buttons for full web component sync
        const regCancellations = document.querySelectorAll('registration-cancellation');
        const el = regCancellations[quoteIdx];
        if (el) {
            const root = el.shadowRoot || el;
            const radios = root.querySelectorAll('input[type="radio"]');
            if (radios.length >= 2) {
                const target = isAccept ? radios[0] : radios[1];
                target.checked = true;
                target.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
                target.click();
            }
        }
    };

    const quotesWithParticipants = localQuotes.map((q, idx) => ({
        ...q,
        name: participants[idx]?.name ?? '',
        description: participants[idx]?.description ?? '',
    }));

    return (
        <>
            <RefundProtectionInfo
                quotes={quotesWithParticipants}
                onSelectionChange={handleQuoteSelection}
                showError={showSelectionError}
                isDirectorView={isDirectorView}
            />
            {/* Web component positioned off-screen: stays fully functional (API calls, events, DOM)
                but is not visible to the user. position:fixed prevents layout impact. */}
            <div style={{ position: 'fixed', left: '-9999px', top: 0, width: '800px' }}>
                <InsuranceComponent
                    client-id={viClientId}
                    offerStateChange={handleOfferStateChange}
                    product={INSURANCE.REGISTRATION_CANCELLATION_INSURANCE}
                    style={{ width: '100%' }}
                >
                    {allPrograms.map((items, _, all) => {
                        const eventDates = items.additionalDates?.filter(el => items.selectedDates.find((id) => el._id === id))?.flatMap(d => [d.startDate, d.endDate])?.sort((a, b) => a - b);
                        let startDate;
                        let endDate;
                        const cartItem = items;
                        const campers = cartItem.campers;
                        const noOfCampers = campers?.length;
                        if (eventDates && eventDates.length > 0) {
                            startDate = new Date(parseInt(eventDates[0])).toISOString().substring(0, 10);
                            endDate = new Date(parseInt(eventDates[eventDates.length - 1])).toISOString().substring(0, 10);
                        } else {
                            startDate = new Date(items.startDate).toISOString().substring(0, 10);
                            endDate = new Date(items.endDate).toISOString().substring(0, 10);
                        }
                        let _multiProgramDiscount = items?.f_multiProgramDiscount ?? 0;
                        let _discountPrice = items?.f_discountPrice ? Math.round((items.f_discountPrice / noOfCampers * 100) / 100) : 0;
                        return (
                            campers.map(camper => {
                                let _couponMoneyValue = camper?.applicableCouponAmount ?? 0;
                                let _applicableGiftCardAmount = camper?.applicableGiftCardAmount ?? 0;
                                const camperDetails = camperList.find(c => c._id === camper.camperId);
                                const _zipCode = billingDetail?.zipCode ?? camper?.zipCode;
                                const additionalExperienceTotal = camper?.additionalExperience?.length > 0
                                    ? camper.additionalExperience.reduce((sum, i) => i.price * i.numberOfBooking, 0)
                                    : 0;
                                return (
                                    <RegistarationComponent
                                        insuranceAmount={camper.f_insuredAmount ?? 0}
                                        couponMoney={_couponMoneyValue + _multiProgramDiscount + _discountPrice + _applicableGiftCardAmount}
                                        additionalExperienceTotal={additionalExperienceTotal}
                                        key={`out-gap-medical-insurance-offer-${cartItem._id}-${camper._id}`}
                                        item={cartItem}
                                        insuranceState={insuranceState}
                                        userEmail={userEmail}
                                        userName={userName}
                                        zipCode={_zipCode}
                                        userLastName={userLastName}
                                        camperDetails={camperDetails}
                                        startDate={startDate}
                                        endDate={endDate}
                                        camperId={camper?.camperId}
                                        noOfCampers={noOfCampers}
                                    />
                                );
                            })
                        );
                    })}
                </InsuranceComponent>
            </div>
        </>
    );
});

export default RegistrationCancellation;
